import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../../../domain/service/payment/payment.service';
import {
  ReturnSummary,
  PaymentRequest,
  PaymentMethodType,
  CardType,
  PaymentConfirmation,
  SavedPaymentMethod,
  GatewayStatusInfo
} from '../../../domain/model/payment/payment.model';
import { NativeIoService } from '../../../core/native/native-io.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-make-payment',
  templateUrl: './make-payment.component.html',
  styleUrls: ['./make-payment.component.css']
})
export class MakePaymentComponent implements OnInit {
  paymentForm!: FormGroup;
  returns: ReturnSummary[] = [];
  selectedReturnIds: number[] = [];
  loading = true;
  processing = false;
  /** Waiting for the gateway to settle a just-paid hosted checkout. */
  confirming = false;
  error: string | null = null;

  /** True when the active gateway is a real processor that redirects to its own payment page. */
  hostedCheckout = false;

  /**
   * True only when the active processor is the mock gateway. "Schedule for later" charges a
   * saved payment method on a future date, but card-on-file isn't implemented for the real
   * processor yet — scheduled payments would still run on mock vault tokens. Hide the option
   * for real processors until Catalis card-on-file is built; remove this gate then.
   */
  isMockGateway = false;

  paymentMethodType = PaymentMethodType;
  cardType = CardType;

  subtotal = 0;
  processingFee = 0;
  total = 0;
  // Installment-plan payment: pay the plan's next amount due instead of the full balance.
  isInstallment = false;
  installmentNextDue: number | null = null;

  // Pay now vs schedule for a future date (charged unattended with a saved method).
  payTiming: 'NOW' | 'SCHEDULE' = 'NOW';
  scheduledDate = '';
  savedMethods: SavedPaymentMethod[] = [];
  savedPaymentMethodId: number | null = null;
  /** Earliest schedulable date (tomorrow, ISO yyyy-MM-dd). */
  readonly minScheduleDate = new Date(Date.now() + 86400000).toISOString().substring(0, 10);

  currentYear = new Date().getFullYear();
  years: number[] = [];

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private route: ActivatedRoute,
    private router: Router,
    private nativeIo: NativeIoService
  ) {
    // Generate next 20 years for card expiration
    for (let i = 0; i < 20; i++) {
      this.years.push(this.currentYear + i);
    }
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadSelectedReturns();
    this.paymentService.getSavedPaymentMethods().subscribe({
      next: (methods) => {
        this.savedMethods = methods ?? [];
        const preferred = this.savedMethods.find(m => m.isDefault) ?? this.savedMethods[0];
        this.savedPaymentMethodId = preferred?.id ?? null;
      },
      error: () => { this.savedMethods = []; }
    });
    this.paymentService.getGatewayStatus().subscribe({
      next: (status: GatewayStatusInfo) => {
        this.hostedCheckout = status.hostedCheckout;
        this.isMockGateway = status.processor === 'MOCK';
        // eCheck is only wired to the mock processor; with a live gateway the server rejects it,
        // so don't offer it — cards go through the hosted checkout.
        if (!this.isMockGateway && this.paymentForm.get('paymentMethod')?.value === 'ACH') {
          this.paymentForm.get('paymentMethod')?.setValue('CREDIT_CARD');
        }
        // Recompute now that we know whether the fee estimate below applies (loadSelectedReturns
        // may have already called calculateFees() before this resolved).
        this.calculateFees();
      },
      error: () => { this.hostedCheckout = false; this.isMockGateway = false; } // fail safe to the legacy in-app card form, schedule hidden
    });
  }

  /**
   * Card tender under a real gateway, redirected to the gateway's hosted payment page instead of
   * collecting card details in-app. Excludes installment-plan payments: checkout/start always
   * charges the full return balance (no partial "next amount due" support yet), so plan payments
   * stay on the legacy in-app form until the backend grows that.
   * ponytail: narrow exclusion for a real backend gap (see Task 8 report), not speculative.
   */
  get isHostedCheckoutCard(): boolean {
    return this.hostedCheckout
      && this.paymentForm.get('paymentMethod')?.value !== 'ACH'
      && !this.isInstallment;
  }

  onContinueToSecurePayment(): void {
    this.processing = true;
    this.error = null;
    // Native lands the gateway on a public page instead of the guarded portal confirmation —
    // the in-app browser has no session and would show a login screen right after paying.
    const returnUrl = this.nativeIo.isNative ? `${environment.webUrl}/checkout-return` : undefined;
    // Web: the hosted page runs in a child window so this page stays put and there is no
    // browser back button into the portal. It must be opened synchronously in the click, or
    // popup blockers eat it; the URL is set once checkout/start answers. Null = blocked →
    // fall back to a full-page redirect (the confirmation page settles it on return).
    const popup = this.nativeIo.isNative ? null
      : window.open('', 'tdtCheckout', 'popup=yes,width=560,height=780,resizable=yes,scrollbars=yes');
    this.paymentService.startCheckout(this.selectedReturnIds, returnUrl).subscribe({
      next: (r) => {
        // Survive the round-trip even if the gateway drops query params on return.
        sessionStorage.setItem('tdt.checkout', JSON.stringify({ transactionId: r.transactionId, ref: r.ref }));
        if (popup) {
          popup.location.href = r.checkoutUrl;
          this.watchCheckoutWindow(popup, r.transactionId, r.ref);
          return;
        }
        // Native: the gateway's return URL points at the website, so a plain redirect would
        // strand the user outside the app. Run checkout in the in-app browser instead and
        // settle the outcome here once it closes.
        this.nativeIo.onExternalUrlClosed(() => this.finishNativeCheckout(r.transactionId, r.ref));
        this.nativeIo.openExternalUrl(r.checkoutUrl);
      },
      error: (err) => {
        popup?.close();
        this.processing = false;
        this.error = err.error?.message || err.error?.error || 'Could not start secure checkout. Please try again.';
      }
    });
  }

  /**
   * Settle a hosted checkout that ran in a child window. The gateway's redirect / back-button
   * pages post a message and close themselves; a window the taxpayer just closes is caught by
   * the poll. Either way we ask the server: approved → confirmation; anything else → cancel
   * on our side (which still finalizes if the gateway approved in the meantime).
   */
  private watchCheckoutWindow(popup: Window, transactionId: string, ref: number | null): void {
    let settled = false;
    const settle = (cancelled: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(poll);
      if (!popup.closed) popup.close();
      // Backed out on the hosted page: nothing to wait for.
      if (cancelled) {
        this.cancelCheckout(transactionId);
        return;
      }
      this.confirmCheckout(transactionId, ref, 1);
    };
    const onMessage = (ev: MessageEvent) => {
      if (ev.origin === window.location.origin && ev.data?.tdtCheckout === 'returned') settle(!!ev.data?.cancelled);
    };
    window.addEventListener('message', onMessage);
    const poll = setInterval(() => { if (popup.closed) settle(false); }, 500);
  }

  /** Statuses the gateway reports for a few seconds after a successful payment. */
  private static readonly CONFIRM_ATTEMPTS = 6;
  private static readonly CONFIRM_RETRY_MS = 2000;

  /**
   * Finalize after the hosted window returns. Right after paying, the gateway can still answer
   * "processing"; that used to be treated as a back-out, so the payment was cancelled on our side
   * ("Payment was cancelled" flashed) until the webhook revived it (QA 2026-10-04). Keep asking
   * for ~10s while it is processing; cancel only on a terminal answer or when we run out.
   */
  private confirmCheckout(transactionId: string, ref: number | null, attempt: number): void {
    this.confirming = true;
    this.paymentService.completeCheckout(transactionId, ref).subscribe({
      next: (confirmation) => {
        this.confirming = false;
        this.clearCheckoutSnapshot();
        this.processing = false;
        this.router.navigate(['/app/payments/confirmation', confirmation.confirmationNumber]);
      },
      error: (err) => {
        const message: string = err?.error?.message || err?.error?.error || '';
        if (message.toLowerCase().includes('still processing') && attempt < MakePaymentComponent.CONFIRM_ATTEMPTS) {
          setTimeout(() => this.confirmCheckout(transactionId, ref, attempt + 1), MakePaymentComponent.CONFIRM_RETRY_MS);
          return;
        }
        this.confirming = false;
        this.cancelCheckout(transactionId);
      },
    });
  }

  private cancelCheckout(transactionId: string): void {
    this.paymentService.cancelCheckout(transactionId).subscribe({
      next: (r) => {
        this.clearCheckoutSnapshot();
        this.processing = false;
        if (r.status === 'COMPLETED' && r.confirmationNumber) {
          this.router.navigate(['/app/payments/confirmation', r.confirmationNumber]);
          return;
        }
        this.error = 'Payment was cancelled — nothing was charged. You can try again.';
      },
      error: (err) => {
        this.clearCheckoutSnapshot();
        this.processing = false;
        this.error = err?.error?.message || err?.error?.error || 'Payment was not completed — nothing was charged. You can try again.';
      },
    });
  }

  /**
   * Resolve a native hosted checkout after the in-app browser is dismissed.
   *
   * Dismissal alone says nothing about the outcome — the app cannot see which page the browser
   * ended on — so ask the gateway. Cancelling (or backing out without paying) is a terminal
   * "not completed", and the taxpayer stays here with their selection intact instead of being
   * sent to a confirmation screen that has nothing to confirm.
   */
  private finishNativeCheckout(transactionId: string, ref: number | null): void {
    this.paymentService.completeCheckout(transactionId, ref).subscribe({
      next: (confirmation) => {
        this.clearCheckoutSnapshot();
        this.processing = false;
        this.router.navigate(['/app/payments/confirmation', confirmation.confirmationNumber]);
      },
      error: (err) => {
        const message: string = err?.error?.message || err?.error?.error || '';
        if (message.toLowerCase().includes('still processing')) {
          // Approved but not settled yet (or a transient gateway lookup miss). The confirmation
          // screen owns the retry loop — hand it the sessionStorage snapshot we already saved.
          this.processing = false;
          this.router.navigate(['/app/payments/confirmation']);
          return;
        }
        this.clearCheckoutSnapshot();
        this.processing = false;
        this.error = message.toLowerCase().includes('cancel')
          ? 'Payment was cancelled — nothing was charged. You can try again.'
          : 'Payment was not completed — nothing was charged. You can try again.';
      }
    });
  }

  /** Drop the pre-redirect snapshot so a later confirmation visit can't re-finalize a dead checkout. */
  private clearCheckoutSnapshot(): void {
    try {
      sessionStorage.removeItem('tdt.checkout');
    } catch {
      /* ignore */
    }
  }

  setTiming(timing: 'NOW' | 'SCHEDULE'): void {
    this.payTiming = timing;
    this.error = null;
  }

  methodLabel(m: SavedPaymentMethod): string {
    if (m.type === PaymentMethodType.ACH) {
      return `${m.bankName || 'Bank account'} ••••${m.accountNumberLast4 || ''}`;
    }
    return `${m.cardType || 'Card'} ••••${m.cardLastFour || ''}`;
  }

  initializeForm(): void {
    this.paymentForm = this.fb.group({
      paymentMethod: ['ACH', Validators.required],

      // ACH / eCheck fields
      routingNumber: [''],
      accountNumber: [''],
      confirmAccountNumber: [''],
      accountType: ['CHECKING'],
      accountHolderName: [''], // "Name on account"
      bankName: [''],

      // Billing address (eCheck)
      billingStreet: [''],
      billingAptSuite: [''],
      billingCity: [''],
      billingState: [''],
      billingZip: [''],
      billingCountry: ['US'],

      // Payer details (eCheck)
      accountClass: ['BUSINESS'], // BUSINESS | PERSONAL
      email: [''],
      phone: [''],
      receiptMethod: ['EMAIL'],   // EMAIL | TEXT
      memo: [''],

      // Card fields
      cardHolderName: [''],
      cardNumber: [''],
      cardExpMonth: [''],
      cardExpYear: [''],
      cvv: [''],
      cardType: [''],
      billingZipCode: [''],

      savePaymentMethod: [false]
    });

    // Listen to payment method changes to update validators
    this.paymentForm.get('paymentMethod')?.valueChanges.subscribe((method) => {
      this.updateValidators(method);
      this.calculateFees();
    });
  }

  updateValidators(paymentMethod: string): void {
    // Clear all validators first
    const achFields = [
      'accountHolderName', 'routingNumber', 'accountNumber', 'confirmAccountNumber',
      'accountType', 'billingStreet', 'billingCity', 'billingState', 'billingZip',
      'billingCountry', 'accountClass', 'email', 'phone', 'receiptMethod'
    ];
    const cardFields = ['cardHolderName', 'cardNumber', 'cardExpMonth', 'cardExpYear', 'cvv', 'billingZipCode'];

    achFields.forEach(field => {
      this.paymentForm.get(field)?.clearValidators();
      this.paymentForm.get(field)?.updateValueAndValidity();
    });

    cardFields.forEach(field => {
      this.paymentForm.get(field)?.clearValidators();
      this.paymentForm.get(field)?.updateValueAndValidity();
    });

    // Add validators based on payment method
    if (paymentMethod === 'ACH') {
      this.paymentForm.get('accountHolderName')?.setValidators([Validators.required]);
      this.paymentForm.get('routingNumber')?.setValidators([Validators.required, Validators.pattern(/^\d{9}$/)]);
      this.paymentForm.get('accountNumber')?.setValidators([Validators.required, Validators.pattern(/^\d{4,17}$/)]);
      this.paymentForm.get('confirmAccountNumber')?.setValidators([Validators.required]);
      this.paymentForm.get('accountType')?.setValidators([Validators.required]);
      this.paymentForm.get('billingStreet')?.setValidators([Validators.required]);
      this.paymentForm.get('billingCity')?.setValidators([Validators.required]);
      this.paymentForm.get('billingState')?.setValidators([Validators.required]);
      this.paymentForm.get('billingZip')?.setValidators([Validators.required, Validators.pattern(/^\d{5}(?:-\d{4})?$/)]);
      this.paymentForm.get('billingCountry')?.setValidators([Validators.required]);
      this.paymentForm.get('accountClass')?.setValidators([Validators.required]);
      this.paymentForm.get('email')?.setValidators([Validators.required, Validators.email]);
      this.paymentForm.get('phone')?.setValidators([Validators.required]);
      this.paymentForm.get('receiptMethod')?.setValidators([Validators.required]);
    } else {
      this.paymentForm.get('cardHolderName')?.setValidators([Validators.required]);
      this.paymentForm.get('cardNumber')?.setValidators([Validators.required, this.cardNumberValidator]);
      this.paymentForm.get('cardExpMonth')?.setValidators([Validators.required, Validators.min(1), Validators.max(12)]);
      this.paymentForm.get('cardExpYear')?.setValidators([Validators.required]);
      this.paymentForm.get('cvv')?.setValidators([Validators.required, Validators.pattern(/^\d{3,4}$/)]);
      this.paymentForm.get('billingZipCode')?.setValidators([Validators.required, Validators.pattern(/^\d{5}$/)]);
    }

    // Update all fields
    Object.keys(this.paymentForm.controls).forEach(key => {
      this.paymentForm.get(key)?.updateValueAndValidity();
    });
  }

  loadSelectedReturns(): void {
    const returnIdsParam = this.route.snapshot.queryParamMap.get('returns');

    if (!returnIdsParam) {
      this.router.navigate(['/app/payments']);
      return;
    }

    this.selectedReturnIds = returnIdsParam.split(',').map(id => parseInt(id, 10));
    this.isInstallment = this.route.snapshot.queryParamMap.get('installment') === 'true';
    this.loading = true;

    // Load return details
    Promise.all(
      this.selectedReturnIds.map(id =>
        this.paymentService.getReturnById(id).toPromise()
      )
    ).then(returns => {
      this.returns = returns.filter((r): r is ReturnSummary => r !== undefined);
      if (this.isInstallment && this.selectedReturnIds.length === 1) {
        // Pay the plan's next amount due (down payment or next installment).
        this.paymentService.getInstallmentPlan(this.selectedReturnIds[0]).subscribe({
          next: (plan) => {
            this.installmentNextDue = plan?.nextDueAmount ?? null;
            this.calculateSubtotal();
            this.calculateFees();
            this.loading = false;
          },
          error: () => { this.calculateSubtotal(); this.calculateFees(); this.loading = false; }
        });
        return;
      }
      this.calculateSubtotal();
      this.calculateFees();
      this.loading = false;
    }).catch(err => {
      console.error('Error loading returns:', err);
      this.error = 'Failed to load return details. Please try again.';
      this.loading = false;
    });
  }

  calculateSubtotal(): void {
    this.subtotal = this.isInstallment && this.installmentNextDue != null
      ? this.installmentNextDue
      : this.returns.reduce((sum, r) => sum + r.balanceDue, 0);
  }

  calculateFees(): void {
    const paymentMethod = this.paymentForm.get('paymentMethod')?.value;

    if (paymentMethod === 'ACH' || this.isHostedCheckoutCard) {
      // Hosted checkout (Catalis) shows its own real convenience fee on its own hosted page —
      // inventing a 2.5% estimate here would just be wrong once a live gateway is active.
      // checkout/start also only ever charges the principal, so no fee belongs in `total` either.
      this.processingFee = 0;
    } else {
      // Legacy in-app card form (MOCK gateway only) — this fee IS what's actually charged.
      this.processingFee = this.subtotal * 0.025; // 2.5% fee
    }

    this.total = this.subtotal + this.processingFee;
  }

  detectCardType(cardNumber: string): void {
    const number = cardNumber.replace(/\s/g, '');

    if (/^4/.test(number)) {
      this.paymentForm.patchValue({ cardType: CardType.VISA });
    } else if (/^5[1-5]/.test(number)) {
      this.paymentForm.patchValue({ cardType: CardType.MASTERCARD });
    } else if (/^3[47]/.test(number)) {
      this.paymentForm.patchValue({ cardType: CardType.AMEX });
    } else if (/^6(?:011|5)/.test(number)) {
      this.paymentForm.patchValue({ cardType: CardType.DISCOVER });
    } else {
      this.paymentForm.patchValue({ cardType: '' });
    }
  }

  /** Validates the card number after stripping the display spaces (13-19 digits). */
  cardNumberValidator = (control: AbstractControl): ValidationErrors | null => {
    const digits = (control.value ?? '').replace(/\s/g, '');
    if (!digits) {
      return null; // empty is handled by Validators.required
    }
    return /^\d{13,19}$/.test(digits) ? null : { cardNumber: true };
  };

  formatCardNumber(event: any): void {
    let value = event.target.value.replace(/\s/g, '');
    let formattedValue = value.match(/.{1,4}/g)?.join(' ') || value;
    this.paymentForm.patchValue({ cardNumber: formattedValue }, { emitEvent: false });
    this.detectCardType(value);
  }

  accountNumbersMatch(): boolean {
    const accountNumber = this.paymentForm.get('accountNumber')?.value;
    const confirmAccountNumber = this.paymentForm.get('confirmAccountNumber')?.value;
    return accountNumber === confirmAccountNumber;
  }

  /** True when the selected returns owe nothing (saved as $0): file with no payment. */
  get isZeroFiling(): boolean {
    return this.subtotal <= 0;
  }

  /** Human-readable message for the server's chronological-order rejection (409). */
  private outOfOrderMessage(err: any): string | null {
    if (err?.status === 409 && err?.error?.code === 'OUT_OF_ORDER') {
      const periods = (err.error.blockingReturns || [])
        .map((b: any) => b.period).filter(Boolean).join(', ');
      return 'Returns must be paid oldest first. Please include your older past-due return(s)'
        + (periods ? ` (${periods})` : '') + ' in this payment.';
    }
    return null;
  }

  private submitSchedule(): void {
    if (!this.scheduledDate || this.scheduledDate < this.minScheduleDate) {
      this.error = 'Pick a future date for the scheduled payment.';
      return;
    }
    if (!this.savedPaymentMethodId) {
      this.error = 'Scheduling a payment requires a saved payment method. Pay now with '
        + '"Save this payment method" checked, then schedule future payments with it.';
      return;
    }
    this.processing = true;
    this.error = null;
    this.paymentService.createScheduledPayment({
      returnIds: this.selectedReturnIds,
      savedPaymentMethodId: this.savedPaymentMethodId,
      scheduledDate: this.scheduledDate
    }).subscribe({
      next: () => {
        this.processing = false;
        sessionStorage.removeItem('tdt.web.basket');
        this.router.navigate(['/app/payments'], { queryParams: { tab: 'scheduled' } });
      },
      error: (err) => {
        this.processing = false;
        this.error = this.outOfOrderMessage(err)
          || err.error?.message || 'Could not schedule the payment. Please try again.';
      }
    });
  }

  submitPayment(): void {
    if (this.payTiming === 'SCHEDULE' && !this.isZeroFiling) {
      this.submitSchedule();
      return;
    }
    if (this.payTiming === 'NOW' && !this.isZeroFiling && this.isHostedCheckoutCard) {
      this.onContinueToSecurePayment();
      return;
    }
    // Zero filing: nothing is due (return saved as $0). Skip the payment form entirely
    // and file the return — the server produces a $0 receipt/confirmation.
    if (this.isZeroFiling) {
      this.processing = true;
      this.error = null;
      const zeroRequest: PaymentRequest = {
        returnIds: this.selectedReturnIds,
        totalAmount: 0,
        savePaymentMethod: false
      };
      this.paymentService.processPayment(zeroRequest).subscribe({
        next: (confirmation: PaymentConfirmation) => {
          this.processing = false;
          this.router.navigate(['/app/payments/confirmation', confirmation.confirmationNumber]);
        },
        error: (err) => {
          console.error('Zero-return filing error:', err);
          this.error = err.error?.message || 'Filing failed. Please try again or contact support.';
          this.processing = false;
        }
      });
      return;
    }

    if (this.paymentForm.invalid) {
      Object.keys(this.paymentForm.controls).forEach(key => {
        this.paymentForm.get(key)?.markAsTouched();
      });
      return;
    }

    const paymentMethod = this.paymentForm.get('paymentMethod')?.value;

    if (paymentMethod === 'ACH' && !this.accountNumbersMatch()) {
      this.error = 'Account numbers do not match. Please verify and try again.';
      return;
    }

    this.processing = true;
    this.error = null;

    const paymentRequest: PaymentRequest = {
      returnIds: this.selectedReturnIds,
      totalAmount: this.total,
      savePaymentMethod: this.paymentForm.get('savePaymentMethod')?.value || false,
      paymentMethod: this.buildPaymentMethod(),
      payerDetails: this.buildPayerDetails(),
      payNextInstallment: this.isInstallment
    };

    this.paymentService.processPayment(paymentRequest).subscribe({
      next: (confirmation: PaymentConfirmation) => {
        this.processing = false;
        this.router.navigate(['/app/payments/confirmation', confirmation.confirmationNumber]);
      },
      error: (err) => {
        console.error('Payment processing error:', err);
        this.error = this.outOfOrderMessage(err)
          || err.error?.message || 'Payment processing failed. Please try again or contact support.';
        this.processing = false;
      }
    });
  }

  buildPayerDetails(): any {
    const v = this.paymentForm.value;
    return {
      billingStreet: v.billingStreet || null,
      billingAptSuite: v.billingAptSuite || null,
      billingCity: v.billingCity || null,
      billingState: v.billingState || null,
      billingZip: v.billingZip || null,
      billingCountry: v.billingCountry || null,
      accountClass: v.accountClass || null,
      email: v.email || null,
      phone: v.phone || null,
      receiptMethod: v.receiptMethod || null,
      memo: v.memo || null
    };
  }

  buildPaymentMethod(): any {
    const formValue = this.paymentForm.value;

    if (formValue.paymentMethod === 'ACH') {
      return {
        type: PaymentMethodType.ACH,
        amount: this.total,
        accountHolderName: formValue.accountHolderName,
        bankName: formValue.bankName,
        accountType: formValue.accountType,
        routingNumber: formValue.routingNumber,
        accountNumberLast4: formValue.accountNumber.slice(-4)
      };
    } else {
      const cardNumber = formValue.cardNumber.replace(/\s/g, '');
      return {
        type: formValue.paymentMethod === 'CREDIT_CARD' ? PaymentMethodType.CREDIT_CARD : PaymentMethodType.DEBIT_CARD,
        amount: this.total,
        cardHolderName: formValue.cardHolderName,
        cardLastFour: cardNumber.slice(-4),
        cardType: formValue.cardType,
        cardExpMonth: formValue.cardExpMonth,
        cardExpYear: formValue.cardExpYear,
        cvv: formValue.cvv,
        billingZipCode: formValue.billingZipCode,
        processingFeePercentage: 2.5,
        processingFeeAmount: this.processingFee
      };
    }
  }

  cancel(): void {
    this.router.navigate(['/app/payments']);
  }

  /** Remove a return from the basket; leave the page if the basket empties. */
  removeReturn(id: number): void {
    this.returns = this.returns.filter(r => r.id !== id);
    this.selectedReturnIds = this.selectedReturnIds.filter(rid => rid !== id);
    // Keep the dashboard's persisted cart in sync — it kept showing the removed items.
    try { sessionStorage.setItem('tdt.web.basket', JSON.stringify(this.selectedReturnIds)); } catch { /* in-memory only */ }
    if (this.returns.length === 0) {
      this.router.navigate(['/app/payments']);
      return;
    }
    this.calculateSubtotal();
    this.calculateFees();
  }

  // --- Itemized charge breakdown across all basket returns ---
  private sumBy(pick: (r: ReturnSummary) => number | undefined): number {
    return this.returns.reduce((s, r) => s + (pick(r) ?? 0), 0);
  }
  get sumTaxableReceipts(): number { return this.sumBy(r => r.taxableRentalReceipts); }
  get sumTaxes(): number { return this.sumBy(r => r.totalTaxes); }
  get sumLateFee(): number { return this.sumBy(r => r.lateFee); }
  get sumLateInterest(): number { return this.sumBy(r => r.lateInterest); }
  get sumAllowance(): number { return this.sumBy(r => r.onlineCollectionAllowance); }

  /** Amount the taxpayer is committing to pay (full balance, incl. card fee). */
  get totalPayment(): number {
    return this.total;
  }

  dismissError(): void {
    this.error = null;
  }
}
